import { subDays } from 'date-fns'
import { mtUrlMap } from '../../hooks/useExternalApp'
import { routes } from '../../router/routes'
import { ANNOUNCEMENT_AUDIENCE, UPDATE_TYPE, isVisibleTo } from './constants'

/*
 * Sample release notes, served through the same slice the API will feed
 * (see WHATS_NEW_API_ENABLED). Titles are illustrative — borrowed from recent
 * tickets and the notification modal copy — not a release plan. Dates are
 * relative to "now" so the fresh/new paths are exercised whenever this runs.
 *
 * Shape = Icaro's draft model (featureId nullable, audience, publishedAt,
 * title, summary, thumbnail — carried as the Ark's `image` object) plus the
 * fields the design needs and the discovery lacks: type, featureName, body,
 * cta, planLabel, media. `featureId` is a real Tenant.features key (skillBite,
 * playlist, skillsPractice, mindsetAi, ...), the same keys the Ark picker
 * stores.
 */
const daysAgo = days => subDays(new Date(), days).toISOString()

const HERO_IMAGE =
  'https://www.mindtools.com/wp-content/uploads/2025/10/New_hero_7-e1761053082855-960x0-c-center.webp'

// MDN's sample clip and its captions, which answer CORS (`*`), so the detail
// page's cross-origin <video crossOrigin> + <track> path runs on the mock. The
// clip is unrelated to the note: it only stands in for a real walkthrough.
const SAMPLE_VIDEO = 'https://developer.mozilla.org/shared-assets/videos/friday.mp4'
const SAMPLE_CAPTIONS = 'https://developer.mozilla.org/shared-assets/misc/friday.vtt'

const buildMockAnnouncements = () => [
  {
    id: 'skill-bites-remember-where-you-stopped',
    type: UPDATE_TYPE.FEATURE,
    featureId: 'skillBite',
    featureName: 'Skill Bites',
    audience: ANNOUNCEMENT_AUDIENCE.ALL,
    publishedAt: daysAgo(3),
    title: 'Skill Bites now remember where you stopped',
    summary: 'Pick up any session exactly where you left it, on any device. Your place is saved as you read, and a Continue button takes you straight back.',
    image: { imgUrl: HERO_IMAGE, altText: '' },
    body: `
      <p>Until now, leaving a Skill Bite halfway meant scrolling back to find your place. From today every session keeps your position, on the web and on your phone, so you can return whenever you have a few minutes.</p>
      <h2>What changed</h2>
      <ul>
        <li>Your place is saved automatically as you read.</li>
        <li>A <strong>Continue</strong> button on the Skill Bites page takes you straight back to the session.</li>
        <li>Progress syncs across devices as soon as you are online.</li>
      </ul>
    `,
    cta: { label: 'Open Skill Bites', to: routes['skill-bites'].path, note: 'Pick up where you stopped.' }
  },
  {
    id: 'a-cleaner-account-settings',
    type: UPDATE_TYPE.IMPROVEMENT,
    featureId: null,
    featureName: 'Account settings',
    audience: ANNOUNCEMENT_AUDIENCE.ALL,
    publishedAt: daysAgo(7),
    title: 'A cleaner Account settings',
    summary: 'Profile, preferences and notifications now live on one page, in the same order on desktop and mobile.',
    image: null,
    body: `
      <p>Account settings used to be spread over several screens that did not always agree with each other. They are now one page with one order of sections, whichever device you are on.</p>
      <h2>What changed</h2>
      <ul>
        <li>Profile, learning preferences and subscription details in one place.</li>
        <li>The same section order on desktop and mobile.</li>
        <li>Every section has its own link, so you can share or bookmark it.</li>
      </ul>
    `,
    cta: { label: 'Open Account settings', to: routes.profile.path }
  },
  {
    id: 'human-made-content-now-labelled',
    type: UPDATE_TYPE.ANNOUNCEMENT,
    featureId: null,
    featureName: null,
    audience: ANNOUNCEMENT_AUDIENCE.ALL,
    publishedAt: daysAgo(19),
    title: 'Human-made content, now labelled',
    summary: 'Articles written, reviewed and approved by our editorial team carry a Human-made badge, so you always know who wrote what you read.',
    image: null,
    body: `
      <p>Every article on Mindtools is planned, researched, written and reviewed by people. We now say so on the page: look for the Human-made badge at the top of an article and in the byline.</p>
      <p>You can read exactly how our content is made, and where we do and do not use AI tools, on the About our content page.</p>
    `,
    cta: { label: 'About our content', to: routes['about-our-content'].path }
  },
  {
    id: 'a-notification-centre-in-the-toolbar',
    type: UPDATE_TYPE.FEATURE,
    featureId: null,
    featureName: 'Notifications',
    audience: ANNOUNCEMENT_AUDIENCE.ALL,
    publishedAt: daysAgo(27),
    title: 'A notification centre in the toolbar',
    summary: 'New content, product news and achievements arrive under the bell. Choose what you hear about in Account settings.',
    image: null,
    body: `
      <p>The bell in the toolbar collects everything that happens while you are away: new content in the topics you follow, product news, your weekly digest and the badges you earn.</p>
      <h2>What changed</h2>
      <ul>
        <li>A panel under the bell with your latest notifications, and a page with the full history.</li>
        <li>A switch for each kind of notification in Account settings.</li>
        <li>Mark everything read in one tap.</li>
      </ul>
    `,
    cta: { label: 'Choose what you hear about', to: routes.profile.path }
  },
  {
    id: 'search-keeps-your-filters',
    type: UPDATE_TYPE.FIX,
    featureId: null,
    featureName: 'Search',
    audience: ANNOUNCEMENT_AUDIENCE.ALL,
    publishedAt: daysAgo(34),
    title: 'Search keeps your filters',
    summary: 'Your filters now stay selected while you search, instead of resetting on every query.',
    image: null,
    body: `
      <p>Changing your search text used to clear the content-type and topic filters you had set. They now stay put until you clear them yourself.</p>
    `,
    cta: null
  },
  {
    id: 'manager-skills-assessment',
    type: UPDATE_TYPE.ANNOUNCEMENT,
    featureId: null,
    featureName: 'Manager Skills Assessment',
    audience: ANNOUNCEMENT_AUDIENCE.CONSUMER,
    publishedAt: daysAgo(41),
    title: 'Manager Skills Assessment',
    summary: 'See where your managers stand today and what to build next. Ask your admin about Enterprise to switch it on.',
    image: null,
    planLabel: 'Available on Enterprise',
    body: `
      <p>The Manager Skills Assessment gives every manager in an organization a picture of their strengths, the gaps that matter most, and a learning path to close them.</p>
      <p>It is part of Mindtools for Enterprise. If your organization would benefit, talk to us.</p>
    `,
    cta: { label: 'Mindtools for organizations', href: mtUrlMap.forbusiness }
  },
  {
    id: 'faster-article-pages',
    type: UPDATE_TYPE.IMPROVEMENT,
    featureId: null,
    featureName: 'Articles',
    audience: ANNOUNCEMENT_AUDIENCE.ALL,
    publishedAt: daysAgo(48),
    title: 'Faster article pages',
    summary: 'Articles now open noticeably faster, even on a slow connection.',
    image: null,
    body: `
      <p>We trimmed what an article page loads before it can show you the text. On a slow connection the difference is a page that reads immediately instead of one that keeps you waiting.</p>
    `,
    cta: null
  },
  {
    id: 'add-your-own-playlist-thumbnail',
    type: UPDATE_TYPE.FEATURE,
    featureId: 'playlist',
    featureName: 'Playlists',
    audience: ANNOUNCEMENT_AUDIENCE.ALL,
    publishedAt: daysAgo(62),
    title: 'Add your own thumbnail to a playlist',
    summary: 'Give a playlist a picture of your choosing, so it stands out from the ones we suggest.',
    image: null,
    body: `
      <p>Playlists you create can now carry an image you upload, instead of the picture of their first item.</p>
      <h2>What changed</h2>
      <ul>
        <li>An Upload thumbnail option when you edit a playlist.</li>
        <li>Your image shows everywhere the playlist appears.</li>
      </ul>
    `,
    cta: { label: 'Open Playlists', to: routes.playlists.path }
  },
  {
    id: 'ai-skills-practice',
    type: UPDATE_TYPE.FEATURE,
    featureId: 'skillsPractice',
    featureName: 'AI Skills Practice',
    audience: ANNOUNCEMENT_AUDIENCE.ENTERPRISE,
    publishedAt: daysAgo(75),
    title: 'Practise difficult conversations with AI Skills Practice',
    summary: 'Rehearse a feedback conversation, a negotiation or a tough one-to-one with an AI partner, and get notes afterwards.',
    image: null,
    body: `
      <p>AI Skills Practice puts you in a realistic conversation with an AI partner who plays the other side, then tells you what worked and what to try next time.</p>
    `,
    cta: { label: 'Try AI Skills Practice', to: routes['ai-skills-practice'].path }
  },
  {
    id: 'quizzes',
    type: UPDATE_TYPE.FEATURE,
    featureId: null,
    featureName: 'Quizzes',
    audience: ANNOUNCEMENT_AUDIENCE.CONSUMER,
    publishedAt: daysAgo(90),
    title: 'Quizzes: check what stuck',
    summary: 'Short quizzes on the skills you read about, with an explanation for every answer.',
    image: null,
    body: `
      <p>Quizzes are a quick way to check what you remember from an article or a Skill Bite, and to spot what is worth a second read.</p>
    `,
    cta: { label: 'Open Quizzes', to: routes.quizzes.path }
  },
  {
    id: 'ask-mcoach-from-anywhere',
    type: UPDATE_TYPE.FEATURE,
    featureId: 'mindsetAi',
    featureName: 'M:Coach',
    audience: ANNOUNCEMENT_AUDIENCE.ALL,
    publishedAt: daysAgo(120),
    title: 'Ask M:Coach from anywhere',
    summary: 'M:Coach now opens from the toolbar on every page, and remembers the conversation while you browse.',
    image: null,
    body: `
      <p>You no longer have to go back to the Home page to ask M:Coach a question. It opens from the toolbar wherever you are, and keeps the thread while you move between pages.</p>
    `,
    media: {
      type: 'video',
      url: SAMPLE_VIDEO,
      poster: null,
      caption: 'A short walkthrough, with captions.',
      captionsUrl: SAMPLE_CAPTIONS
    },
    cta: null
  },
  {
    id: 'keyboard-and-screen-reader-fixes',
    type: UPDATE_TYPE.IMPROVEMENT,
    featureId: null,
    featureName: 'Accessibility',
    audience: ANNOUNCEMENT_AUDIENCE.ALL,
    publishedAt: daysAgo(140),
    title: 'Keyboard navigation and screen-reader fixes',
    summary: 'Clearer focus, better labels and a logical tab order across the toolbar, cards and dialogs.',
    image: null,
    body: `
      <p>Following an independent accessibility audit we fixed the issues it found across the toolbar, content cards and dialogs.</p>
      <h2>What changed</h2>
      <ul>
        <li>A visible focus ring on every interactive element.</li>
        <li>Names for icon-only buttons.</li>
        <li>A tab order that follows the visual order.</li>
      </ul>
    `,
    cta: { label: 'Accessibility statement', to: routes['a11y-statement'].path }
  }
]

/** Reverse-chron, audience-filtered, one page. Mirrors the API envelope. */
export const buildMockAnnouncementsPage = ({ from = 0, size = 10, audience } = {}) => {
  const visible = buildMockAnnouncements()
    .filter(item => isVisibleTo(item, { audience }))
    .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
    // `sk` is what the home rails key items on (DynamoDB sort key); the API
    // will carry one, the mock mirrors it.
    .map(item => ({ ...item, sk: item.id }))
  return {
    items: visible.slice(from, from + size),
    total: visible.length,
    from
  }
}

export const findMockAnnouncement = (id, { audience } = {}) => {
  const item = buildMockAnnouncements().find(candidate => candidate.id === id)
  return item && isVisibleTo(item, { audience }) ? { ...item, sk: item.id } : null
}
